import { flatMap } from "lodash"
import { CCUtils } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { Buttons } from "../../../generic-sprites/Buttons"
import { GenericAssets } from "../../../generic-sprites/GenericAssets"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { MakeVaperImages } from "../MakeYourOwnVaperAssets"
import { MakeVaperAgreement } from "../part-2/MakeYourOwnVaperPartTwoTypes"
import { MakeVaperSpudPosition, MakeVaperSpudSize } from "../types/MakeVaperScreenValueTypes"
import { MakeVaperChoices, MakeVaperPart } from "../types/MakeYourOwnVaperTypes"
import { MakeVaperSpudPositions } from "../utils/MakeVaperSpudPositions"

export class MakeVaperSpudSprite {
    static classVaperParts: MakeVaperChoices = {
        smart: false,
        cool: false,
        popular: false,
        exciting: false,
        athletic: false
    }

    static checkAgreement = (spud: MakeVaperChoices): MakeVaperAgreement => {
        const parts = CCUtils.enumValues(MakeVaperPart)
        const equals = part => MakeVaperSpudSprite.classVaperParts[part] === spud[part]
        switch (true) {
            case parts.every(equals):
                return MakeVaperAgreement.AGREE
            case parts.some(equals):
                return MakeVaperAgreement.ALMOST_AGREE
            default:
                return MakeVaperAgreement.DISAGREE
        }
    }

    spud: Phaser.Sprite
    ecig: Phaser.Sprite
    partsGroup: Phaser.Group
    titleGroup: Phaser.Group
    vaperPartsMap: Map<MakeVaperPart, { yes: Phaser.Sprite, no: Phaser.Sprite }>
    size: MakeVaperSpudSize

    constructor(
        private game: Phaser.Game,
        private group: Phaser.Group,
        private position: MakeVaperSpudPosition
    ) {
        const {x, y} = MakeVaperSpudPositions.spudPositions.get(position)
        this.size = MakeVaperSpudSize.LARGE
        this.partsGroup = group
        this.spud = Sprites.add(game, MakeVaperImages.SPUD, {scale: this.size, debug: CCUtils.debug, group: this.partsGroup, x, y})

        this.vaperPartsMap = new Map()
        this.setVaperMapParts()
        this.setEcigPart()

    }

    get vaperParts(): Array<Phaser.Sprite> {
        return flatMap(Array.from(this.vaperPartsMap.values()), (value: { yes: Phaser.Sprite, no: Phaser.Sprite }) => [value.yes, value.no])
    }

    set viewEcig(visible: boolean) {
        this.ecig.visible = visible
    }

    set scale(scale: MakeVaperSpudSize) {
        this.size = scale
        this.partsGroup.forEach((sprite: Phaser.Sprite) => {
            switch (sprite.key) {
                case GenericAssets.ECIG_CROPPED:
                    Scale.scaleSprite(sprite, MakeVaperSpudPositions.spudEcigScale.get(scale))
                    this.placeEcigPart()
                    break
                case MakeVaperImages.SPUD:
                    Scale.scaleSprite(sprite, scale)
                    break
                default:
                    Scale.scaleSprite(sprite, scale)
                    this.placeVaperPart(sprite)
            }
        })
    }

    setPartResponse = (part: MakeVaperPart, response: boolean) => {
        const {yes, no} = this.vaperPartsMap.get(part)
        yes.visible = response
        no.visible = !response
    }

    showAllSpudParts = (data: MakeVaperChoices) => {
        return CCUtils.enumValues(MakeVaperPart)
                      .forEach((part: MakeVaperPart) => {
                          log.debug(`Show all spud parts part ${part} answered yes ${data[part]}`)
                          this.setPartResponse(part, data[part])
                      })
    }

    showSpud = (visible: boolean) => {
        this.partsGroup.visible = visible
        this.destroyTitle()
    }

    moveSpud = (x: number, y: number) => {
        const diffX = x - this.spud.x
        const diffY = y - this.spud.y
        this.spud.x = x
        this.spud.y = y

        this.partsGroup.forEach((sprite: Phaser.Sprite) => {
            // Will only not move non-sprite elements and the spud sprite.
            if (sprite.key !== MakeVaperImages.SPUD && sprite.type === 0) {
                sprite.x += diffX
                sprite.y += diffY
            }
        })
    }

    setSpudPosition = (position: MakeVaperSpudPosition) => {
        const {x, y} = MakeVaperSpudPositions.spudPositions.get(position)
        this.moveSpud(x, y)
    }

    destroyTitle = () => {
        if (this.titleGroup) {
            this.titleGroup.destroy(true)
        }
    }

    private setVaperMapParts = () => {
        this.vaperPartsMap.set(MakeVaperPart.SMART, this.createYesNoParts(MakeVaperImages.SMART, MakeVaperImages.DUNCE))
        this.vaperPartsMap.set(MakeVaperPart.ATHLETIC, this.createYesNoParts(MakeVaperImages.BUFF_ARMS, MakeVaperImages.WEAK_ARMS))
        this.vaperPartsMap.set(MakeVaperPart.POPULAR, this.createYesNoParts(MakeVaperImages.HAPPY_POTATOES, MakeVaperImages.SAD_POTATOES))
        this.vaperPartsMap.set(MakeVaperPart.COOL, this.createYesNoParts(MakeVaperImages.SUNGLASSES, MakeVaperImages.BIB))
        this.vaperPartsMap.set(MakeVaperPart.EXCITING, this.createYesNoParts(MakeVaperImages.EXCITED_POTATOES, MakeVaperImages.BORED_POTATOES))
    }

    private createYesNoParts = (yes: MakeVaperImages, no: MakeVaperImages) => ({yes: this.createVaperPart(yes), no: this.createVaperPart(no)})

    private vaperPartOnClick = (part: Phaser.Sprite, asset: MakeVaperImages) => () => {
        const widthPadding = 20 * Scale.scaleAmount()
        const heightPadding = 8 * Scale.scaleAmount()
        const text = MakeVaperSpudPositions.spriteTitlePositions.get(asset)

        this.destroyTitle()
        this.titleGroup = this.game.add.group()
        const titleBox = Sprites.add(this.game, GenericAssets.TEXT_BOX, {
            group: this.titleGroup, debug: CCUtils.debug,

        })
        const titleText = PhaserText.create(this.game, text, {debug: CCUtils.debug, fontPx: 18, group: this.titleGroup})
        titleBox.width = titleText.width + widthPadding * 3
        titleBox.height = titleText.height + heightPadding * 2
        switch (asset) {
            case MakeVaperImages.SMART:
            case MakeVaperImages.DUNCE:
                titleBox.x = Scale.x(0.805)
                titleBox.y = Scale.y(0.286)
                break
            case MakeVaperImages.SUNGLASSES:
            case MakeVaperImages.BIB:
                titleBox.x = Scale.x(0.658)
                titleBox.y = Scale.y(0.435)
                break
            case MakeVaperImages.HAPPY_POTATOES:
                titleBox.x = Scale.x(0.756)
                titleBox.y = Scale.y(0.832)
                break
            case MakeVaperImages.SAD_POTATOES:
                titleBox.x = Scale.x(0.706)
                titleBox.y = Scale.y(0.209)
                break
            case MakeVaperImages.EXCITED_POTATOES:
            case MakeVaperImages.BORED_POTATOES:
                titleBox.x = Scale.x(0.645)
                titleBox.y = Scale.y(0.425)
                break
            case MakeVaperImages.BUFF_ARMS:
            case MakeVaperImages.WEAK_ARMS:
                titleBox.x = Scale.x(0.657)
                titleBox.y = Scale.y(0.768)
                break
            default:
                throw new Error("Unexpected vaper image: " + asset)
        }
        titleBox.x -= titleBox.width / 2
        titleBox.y -= titleBox.height / 2

        if (this.position === MakeVaperSpudPosition.LEFT) {
            const spudDeltaX =
                MakeVaperSpudPositions.spudPositions.get(MakeVaperSpudPosition.RIGHT).x -
                MakeVaperSpudPositions.spudPositions.get(MakeVaperSpudPosition.LEFT).x
            titleBox.x -= spudDeltaX
        }

        Sprites.centerSpriteInSprite(titleBox, titleText)
    }


    private setEcigPart = () => {
        this.ecig = Sprites.add(this.game, GenericAssets.ECIG_CROPPED, {group: this.partsGroup, scale: 0.3, debug: CCUtils.debug})
        this.ecig.angle = 200
        this.ecig.anchor.setTo(1, 0.5)
        this.placeEcigPart()
    }

    private createVaperPart = (asset: MakeVaperImages, visible = false): Phaser.Sprite => {
        const part = Sprites.add(this.game, asset, {group: this.partsGroup, scale: this.size, debug: CCUtils.debug})
        Buttons.makeSpriteButton(part, this.vaperPartOnClick(part, asset))
        part.visible = visible
        this.placeVaperPart(part)
        return part
    }

    private placeVaperPart = (part: Phaser.Sprite) => {
        Sprites.centerSpriteInSprite(this.spud, part)
        const {xDelta, yDelta} = MakeVaperSpudPositions.spudPartPositions.get(this.size).get(part.key as string)
        part.x += xDelta
        part.y += yDelta
    }

    private placeEcigPart = () => {
        Sprites.centerSpriteInSprite(this.spud, this.ecig)
        const {xDelta, yDelta} = MakeVaperSpudPositions.spudEcigPositions.get(this.size)
        this.ecig.x += xDelta
        this.ecig.y += yDelta
    }
}
